// "A Blue commotion": Times Square after the rain → a drop → the whirlpool → the painting rushing → blue peace.
export const blueCommotion = {
  slug: "a-blue-commotion",
  duration: 27,
  stages: [
    { id: "street", at: 0, hint: "tap the puddle" },
    { id: "strike", at: 7, hint: "tap the puddle" },
    { id: "inside", at: 11.5, hint: "" },
    { id: "peace", at: 19.5, hint: "" },
  ],
  line: { text: "Whatever strikes you from outside ends up striking you inside.", from: 8.6, to: 18.4 },
  eye: { src: "/arcs/eye.webp", from: 0.2, to: 4.6, blinks: [2.2, 3.4] },
  drop: { at: 6.4, land: 7, x: 0, z: -2.3 },
  touch: { kind: "ripple", until: 11.5 },
  ambient: { scene: "city", until: 19.5 },
  look: { start: -0.5, lift: [11.5, 15], end: 0.06 },
  calm: { duration: 13, fade: [4, 8.5], line: [5, 11], backdrop: "street", tint: "#0F2C5C" },
  // rush speed of the strokes over time: [t, speed]
  speed: [[0, 0], [11, 0], [12.5, 1], [18, 0.85], [22, 0.18], [27, 0.12]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uRip[6];
#define uShift smoothstep(7.5, 14.0, uT)
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
// the city's neon, turning toward the painting's blues, greys and silvers as the reveal takes hold
vec3 neon(float h){
  vec3 c = h < 0.34 ? vec3(1.0, 0.18, 0.62) : h < 0.67 ? vec3(1.0, 0.74, 0.22) : vec3(0.15, 0.9, 1.0);
  vec3 p = h < 0.34 ? vec3(0.2, 0.42, 0.95) : h < 0.67 ? vec3(0.78, 0.82, 0.88) : vec3(0.45, 0.55, 0.68);
  return mix(c, p, uShift);
}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
vec2 rot(vec2 p, float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c) * p; }
// sign columns: N per turn, each a thin vertical neon bar
const float N = 22.0;
vec4 signAt(float th){
  float u = th / 6.28318 * N;
  float id = floor(u);
  float h = hash(id + 3.0);
  float off = 0.25 + 0.5 * hash(id + 9.0);
  float w = 0.035 + 0.05 * hash(id + 17.0);
  float bar = 1.0 - smoothstep(w * 0.5, w, abs(fract(u) - off));
  return vec4(neon(h), bar * step(0.28, hash(id + 31.0)));
}
float ripples(vec2 p, out float ring){
  float disp = 0.0; ring = 0.0;
  for (int i = 0; i < 6; i++){
    vec4 r = uRip[i];
    float age = uT - r.z;
    if (r.w <= 0.0 || age < 0.0 || age > 6.0) continue;
    float d = length(p - r.xy);
    float front = age * 1.5;
    float env = exp(-age * 0.75) * r.w * smoothstep(front + 0.5, front, d) * smoothstep(0.0, 0.3, d + 0.2);
    float w = sin((d - front) * 16.0);
    disp += w * env * 0.06;
    ring += max(w, 0.0) * env * smoothstep(front - 1.2, front, d);
  }
  return disp;
}
void main(){
  float strikeK = smoothstep(7.4, 11.5, uT);
  float insideK = smoothstep(11.0, 12.5, uT);
  float peace = smoothstep(19.0, 24.0, uT);
  vec2 p0 = vec2(0.0, -2.3);
  vec3 street;
  vec2 local;
  float dist;
  float n = snoise(vec3(vPos.xz * 0.45 + vPos.y * 0.3, uT * 0.05)) * 0.5 + 0.5;
  if (uSurface < 0.5) {
    float th = atan(vPos.x, -vPos.z);
    float y = vPos.y;
    float bh = 3.6 + 3.4 * hash(floor((th + 3.2) / 6.28318 * 9.0));
    vec3 sky = mix(vec3(0.07, 0.05, 0.13), vec3(0.02, 0.02, 0.06), smoothstep(4.0, 10.0, y));
    vec3 bld = vec3(0.025, 0.028, 0.05);
    vec2 win = vec2(th / 6.28318 * 140.0, y * 3.2);
    float lit = step(0.82, hash2(floor(win))) * step(0.2, fract(win.x)) * step(0.25, fract(win.y)) * step(y, bh - 0.3) * step(1.2, y);
    bld += lit * vec3(0.32, 0.24, 0.12) * 0.5;
    street = mix(bld, sky, step(bh, y));
    float swirlTh = th + strikeK * strikeK * 0.35 * sin(y * 0.8 + uT * 1.7);
    vec4 s = signAt(swirlTh);
    float band = smoothstep(1.2, 1.6, y) * (1.0 - smoothstep(bh - 0.6, bh, y));
    float flick = 0.85 + 0.15 * sin(uT * 3.0 + hash(floor(th * 3.5)) * 40.0);
    street += s.rgb * s.a * band * 1.1 * flick;
    street += vec3(0.25, 0.12, 0.3) * exp(-abs(y - 1.0) * 1.3) * 0.25;
    local = vec2(th * uRadius, y - uCy);
    dist = 4.2 + abs(th) * uRadius * 0.5 + abs(y - 1.0) * 0.55;
    local.x += uPhase * 1.4 + 0.25 * sin(local.y * 0.9 + uPhase * 0.7);
  } else {
    vec2 xz = vPos.xz;
    float dp = length(xz - p0);
    float tw = strikeK * strikeK * 5.5 / (dp + 0.6);
    vec2 q = p0 + rot(xz - p0, tw);
    // small whirlpools turning in the puddles
    for (int i = 0; i < 4; i++){
      float a = float(i) * 1.7 + 0.6;
      vec2 c = vec2(sin(a), -cos(a)) * (2.2 + float(i) * 0.7);
      float dc = length(q - c);
      q = c + rot(q - c, (uT * 1.4 + float(i)) * exp(-dc * 1.8) * 1.6);
    }
    float ring;
    float disp = ripples(xz, ring);
    float th = atan(q.x, -q.y) + disp;
    float r = length(q);
    if (uSurface < 1.5) {
      float wet = smoothstep(0.42, 0.6, snoise(vec3(xz * 0.35, 1.3)) * 0.5 + 0.5 + 0.25 * (1.0 - smoothstep(0.0, 3.5, dp)));
      vec4 s = signAt(th + 0.012 * sin(r * 7.0 - uT * 1.4));
      float streak = s.a * smoothstep(0.6, 1.6, r) * (0.55 + 0.45 * smoothstep(uRadius, 1.5, r));
      vec3 asphalt = vec3(0.03, 0.032, 0.045) + 0.02 * n;
      street = asphalt + s.rgb * streak * mix(0.12, 0.95, wet);
      street += vec3(0.75, 0.85, 1.0) * ring * 0.55 * wet;
      street += vec3(0.3, 0.15, 0.4) * wet * 0.05;
      dist = dp;
    } else {
      street = vec3(0.02, 0.02, 0.05);
      dist = 14.0 + (uRadius - length(xz)) * 0.6;
    }
    local = rot(xz, uPhase * 0.22 + (uSurface > 1.5 ? -uPhase * 0.1 : 0.0));
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float l = dot(paint, vec3(0.299, 0.587, 0.114));
  paint = clamp(mix(vec3(l), paint, 1.25) * 1.08, 0.0, 1.0);
  vec2 sc = vec2(local.x * 2.2, local.y * 7.0) + vec2(0.0, 0.0);
  vec2 cell = floor(sc);
  float hs = hash2(cell);
  float fl = fract(uT * (0.5 + hs * 0.9) + hs * 7.0);
  vec2 f = fract(sc) - 0.5;
  float dash = (1.0 - smoothstep(0.02, 0.06, abs(f.y + 0.08 * sin(f.x * 6.0)))) * (1.0 - smoothstep(0.25, 0.45, abs(f.x)));
  float spark = dash * step(0.86, hs) * (1.0 - smoothstep(0.0, 0.09, fl)) * insideK * (1.0 - 0.75 * peace);
  float reach = (uT - 11.3) * 3.4;
  float m = 1.0 - smoothstep(reach - 1.4, reach, dist + n * 1.6);
  m *= insideK;
  float lp = dot(paint, vec3(0.299, 0.587, 0.114));
  vec3 silver = mix(vec3(lp) * vec3(0.86, 0.92, 1.04), paint, 0.55);
  paint = mix(paint, silver, smoothstep(13.0, 19.0, uT) * 0.45);
  vec3 deep = paint * vec3(0.55, 0.72, 1.12) + vec3(0.0, 0.03, 0.12);
  vec3 col = mix(paint, deep, 0.35 * peace);
  float lw = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(0.98, 0.99, 1.0), smoothstep(0.82 - 0.3 * peace, 1.0, lw) * 0.85 * peace);
  col += vec3(1.0) * spark;
  col += m * (1.0 - m) * vec3(0.5, 0.75, 1.0) * 0.8;
  col = mix(street, col, m);
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`,
};
